import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { CaseManagementService } from '../../services/case-management.service';
import { UtilsService } from '../../edge/utils.service';
import { catchError, map, mergeMap, of, share, switchMap } from 'rxjs';
import { CaseAnnotationActions } from '@states/case-annotation/case-annotation.actions';
import { SharedActions } from '@states/shared/shared.action-types';

@Injectable()
export class CaseAnnotationEffect {
  private readonly actions$ = inject(Actions);
  private readonly caseManagementService = inject(CaseManagementService);
  private readonly utilService = inject(UtilsService);

  public beforeGetAnnotations$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CaseAnnotationActions.beforeGetAnnotations),
      map(({ entityId }) => CaseAnnotationActions.getAnnotations({ entityId })),
    ),
  );

  public getAnnotations$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CaseAnnotationActions.getAnnotations),
      switchMap(({ entityId }) => this.caseManagementService.getAnnotationsByEntityId(entityId).pipe(
        map((annotations) => CaseAnnotationActions.getAnnotationsSuccess({ annotations })),
        catchError((error) => [
          CaseAnnotationActions.getAnnotationsFail(),
          SharedActions.showMessage({ error: this.utilService.errMessage(error) }),
        ]),
      )),
      share(),
    ),
  );

  public createAnnotation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CaseAnnotationActions.createAnnotation),
      switchMap(({ data, screenshotUpload }) => {
        const uploadFilename = data._id && data.timeline !== undefined
          ? `${data.timeline}-${data._id}.png`
          : null;
        const upload$ = screenshotUpload?.file && uploadFilename
          ? this.caseManagementService.generateAnnotationUploadUrl({
            caseId: screenshotUpload.caseId,
            sessionId: screenshotUpload.sessionId,
            filename: uploadFilename,
          }).pipe(
            switchMap(({ url, headers }) => {
              const screenshotFile = new File(
                [screenshotUpload.file],
                uploadFilename,
                { type: screenshotUpload.file.type || 'image/png' },
              );

              return this.caseManagementService.uploadAnnotationToSignedUrl({
                url,
                file: screenshotFile,
                filename: screenshotFile.name,
                headers,
              });
            }),
          )
          : of(null);

        return upload$.pipe(
          switchMap(() => this.caseManagementService.createAnnotation(data).pipe(
            map((annotation) => CaseAnnotationActions.createAnnotationSuccess({
              annotation,
              caseId: data.caseId,
              archiveId: data.entityId,
            })),
            catchError((error) => {
              if (!screenshotUpload?.file || !uploadFilename) {
                return [
                  CaseAnnotationActions.createAnnotationFail(),
                  SharedActions.showMessage({ error: this.utilService.errMessage(error) }),
                ];
              }

              return this.caseManagementService.deleteAnnotationUpload({
                caseId: screenshotUpload.caseId,
                sessionId: screenshotUpload.sessionId,
                filename: uploadFilename,
              }).pipe(
                map(() => CaseAnnotationActions.createAnnotationFail()),
                catchError(() => of(CaseAnnotationActions.createAnnotationFail())),
                mergeMap((action) => [
                  action,
                  SharedActions.showMessage({ error: this.utilService.errMessage(error) }),
                ]),
              );
            }),
          )),
          catchError((error) => [
            CaseAnnotationActions.createAnnotationFail(),
            SharedActions.showMessage({ error: this.utilService.errMessage(error) }),
          ]),
        );
      }),
      share(),
    ),
  );

  public editAnnotation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CaseAnnotationActions.editAnnotation),
      switchMap(({ id, data, archiveId, screenshotUpload }) => {
        const uploadFilename = screenshotUpload?.file && data.timeline !== undefined
          ? `${data.timeline}-${id}.png`
          : null;
        const upload$ = screenshotUpload?.file
          ? this.caseManagementService.generateAnnotationUploadUrl({
            caseId: screenshotUpload.caseId,
            sessionId: screenshotUpload.sessionId,
            filename: uploadFilename ?? '',
          }).pipe(
            switchMap(({ url, headers }) => {
              const screenshotFile = new File(
                [screenshotUpload.file],
                uploadFilename ?? '',
                { type: screenshotUpload.file.type || 'image/png' },
              );

              return this.caseManagementService.uploadAnnotationToSignedUrl({
                url,
                file: screenshotFile,
                filename: screenshotFile.name,
                headers,
              });
            }),
          )
          : of(null);

        return upload$.pipe(
          switchMap(() => this.caseManagementService.updateAnnotation(id, data).pipe(
            map((annotation) => CaseAnnotationActions.editAnnotationSuccess({
              annotation,
              caseId: data.caseId,
              archiveId,
            })),
            catchError((error) => {
              if (!screenshotUpload?.file || !uploadFilename) {
                return [
                  CaseAnnotationActions.editAnnotationFail(),
                  SharedActions.showMessage({ error: this.utilService.errMessage(error) }),
                ];
              }

              return this.caseManagementService.deleteAnnotationUpload({
                caseId: screenshotUpload.caseId,
                sessionId: screenshotUpload.sessionId,
                filename: uploadFilename,
              }).pipe(
                map(() => CaseAnnotationActions.editAnnotationFail()),
                catchError(() => of(CaseAnnotationActions.editAnnotationFail())),
                mergeMap((action) => [
                  action,
                  SharedActions.showMessage({ error: this.utilService.errMessage(error) }),
                ]),
              );
            }),
          )),
          catchError((error) => [
            CaseAnnotationActions.editAnnotationFail(),
            SharedActions.showMessage({ error: this.utilService.errMessage(error) }),
          ]),
        );
      }),
      share(),
    ),
  );

  public deleteAnnotation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CaseAnnotationActions.deleteAnnotation),
      switchMap(({ id, caseId, archiveId }) => this.caseManagementService.deleteAnnotation(id).pipe(
        map(() => CaseAnnotationActions.deleteAnnotationSuccess({ id, caseId, archiveId })),
        catchError((error) => [
          CaseAnnotationActions.deleteAnnotationFail({ id }),
          SharedActions.showMessage({ error: this.utilService.errMessage(error) }),
        ]),
      )),
      share(),
    ),
  );
}
